import dayjs from '~/helpers/dayjsHelper';
import type { Dayjs } from 'dayjs';

export const formats = {
  DATE: 'MMM D',
  LONG_DATE: 'MMM D, YYYY',
  FULL_MONTH_DATE: 'MMMM D, YYYY',
  MEDIUM_DATE: 'MMM D, YYYY',
  CHAT_DATE: 'ddd, MMMM D',
  DATETIME: 'MMM D, h:mm a z',
  US_DATE: 'MM/DD/YYYY',
  SHORT_US_DATETIME: 'M/D h:mm a',
  SIGNATURE_DATE: 'MMM D, YYYY | h:mm a z',
  TRACKING_DATE: 'MMM D, YYYY h:mm A',
  MONTH: 'MMMM',
  HOUR: 'HH:mm',
  FULL_DAY: 'dddd',
  DATE_PT_FORMAT: 'MM/DD/YYYY h:mm A [PT]',
  SHORT_DATE_FORMAT: 'DD MMM, YYYY',
  APPOINTMENT_TIME_FORMAT: 'h:mm a z'
} as const;

export type DateFormat = typeof formats[keyof typeof formats];

export const initTimezone = (zone: string): void => dayjs.tz.setDefault(zone);

export const formatDate = (date: string | number | Date, format: string): string =>
  dayjs(date).format(format);

export const formatUtcDate = (date: string | number | Date, format: string): string =>
  dayjs.utc(date).format(format);

export const formatTimestamp = (timestamp: number, format: string): string =>
  dayjs.unix(timestamp).format(format);

export const formatTimestampUtc = (timestamp: number, format: string): string =>
  dayjs.unix(timestamp).utc().format(format);

export const timestamp = (date?: number | Date): number => {
  const dateValue = date instanceof Date ? date.getTime() : (date || Date.now());
  return Math.floor(dateValue / 1000);
};

export const dateFromTimestamp = (timestamp: number): Date =>
  new Date(timestamp * 1000);

// receives a date as INT and returns an ISO string
// for NULL timestamp, returns an empty string
export const isoFromTimestamp = (timestamp: number | null | undefined): string =>
  timestamp ? dayjs.unix(timestamp).toISOString() : "";

// receives a date as STRING and returns an INT or NULL
export const timestampFromIso = (date: string | null | undefined): number | null =>
  date ? Math.floor((new Date(date).getTime()) / 1000) : null;

export const timeSince = (date: number): string => {
  let seconds: number = timestamp() - date;

  let interval = seconds / 31536000;

  if (interval > 1) {
    if (interval < 2) return "1 year";
    return Math.floor(interval) + " years";
  }

  interval = seconds / 2592000;

  if (interval > 1) {
    if (interval < 2) return "1 month";
    return Math.floor(interval) + " months";
  }

  interval = seconds / 86400;

  if (interval > 1) {
    if (interval < 2) return "1 day";
    return Math.floor(interval) + " days";
  }

  interval = seconds / 3600;

  if (interval > 1) {
    if (interval < 2) return "1 hour";
    return Math.floor(interval) + " hours";
  }

  interval = seconds / 60;

  if (interval > 1) {
    if (interval < 2) return "1 minute";
    return Math.floor(interval) + " minutes";
  }

  return Math.floor(seconds) === 0 ? "now" : (Math.floor(seconds) === 1 ? "1 second" : Math.floor(seconds) + " seconds");
}

// compares two timestamps and returns true if they represent the same date
// if only 1 arg is received, compares it to the current date
export const isSameDate = (ts1: number, ts2: number = timestamp(new Date())): boolean => {
  return dayjs(dateFromTimestamp(ts1)).isSame(dayjs(dateFromTimestamp(ts2)), "day");
}

export const isPast7days = (ts: number): boolean => {
  return dayjs(dateFromTimestamp(ts)).isAfter(dayjs().subtract(7, 'd'))
}

export const getMessageDateFormat = (ts: number): string => {
  if (isSameDate(ts)) return formats.HOUR;
  if (isPast7days(ts)) return formats.FULL_DAY;
  return formats.US_DATE;
}

// receives a Date and returns an object with the date components as keys
export interface DateComponents {
  year: number;
  month: number;
  day: number;
  hours: number;
  minutes: number;
}

export const extractDateComponents = (date: Date): DateComponents => {
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();
  const hours = date.getHours();
  const minutes = date.getMinutes();

  return { year, month, day, hours, minutes };
}

// receives a Date and changes it's time zone to the user time zone, while keeping the values for year, month, day, hours and minutes
export const localDateToTimezoneTs = (date: Date): number => {
  // extract the values selected by the user
  const { year, month, day, hours, minutes } = extractDateComponents(date);

  // by creating a dayjs object out of the selected data, we are initializing it with the timezone of the user
  const tzDate = dayjs().year(year).month(month).date(day).hour(hours).minute(minutes).second(0).millisecond(0);

  return tzDate.unix();
};

// receives a Dayjs object and returns an object with the date components as keys
export const extractDayjsComponents = (dayjsObj: Dayjs): DateComponents => {
  const year = dayjsObj.year();
  const month = dayjsObj.month();
  const day = dayjsObj.date();
  const hours = dayjsObj.hour();
  const minutes = dayjsObj.minute();

  return { year, month, day, hours, minutes };
};

// receives a unix timestamp and changes it's time zone to the local (browser) time zone, while keeping the values for year, month, day, hours and minutes
// returns a Date
export const timezoneTsToLocalDate = (ts: number): Date => {
  // create dayjs from unix timestamp
  const selectedDayjs = dayjs.unix(ts);

  // extract the values selected by the user
  const { year, month, day, hours, minutes } = extractDayjsComponents(selectedDayjs);

  // the new date will be initialized with the local time zone
  return new Date(year, month, day, hours, minutes, 0, 0);
};

//
export const daysAgoTs = (days: number, date: string | number | Date): number =>
  daysAgo(days, date).unix();

export const daysAgo = (days: number, date: string | number | Date): Dayjs =>
  dayjs(date).subtract(days, 'days');
